import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { communicationsApi } from './api';
import type {
  BillReceiptData,
  Campaign,
  CampaignListParams,
  CampaignListResult,
  CampaignStatus,
  CommLogListParams,
  CommLogListResult,
  CommLogsSummary,
  CommLogType,
  CommStats,
  CommTemplateListParams,
  CommTemplateListResult,
  CreateCampaignInput,
  CreateDealInput,
  CreateSocialPostInput,
  CreateTemplateInput,
  DealListResult,
  FeedbackListParams,
  FeedbackListResult,
  FeedbackStats,
  ProductOption,
  SendInvoiceInput,
  SocialPostListResult,
  UpdateTemplateInput,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

// ── Stats ─────────────────────────────────────────────────────────────────────

export function useCommStats(): UseQueryResult<CommStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'stats'],
    queryFn: () => communicationsApi.stats(),
    staleTime: 30_000,
  });
}

// ── Logs ──────────────────────────────────────────────────────────────────────

export function useCommLogs(params: CommLogListParams): UseQueryResult<CommLogListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'communications', tenantId, 'logs',
      params.page ?? 1,
      params.channel ?? '',
      params.status ?? '',
      params.type ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
      params.search ?? '',
    ],
    queryFn: () => communicationsApi.logs(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCommLogsSummary(type?: CommLogType): UseQueryResult<CommLogsSummary> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'logs-summary', type ?? ''],
    queryFn: () => communicationsApi.logsSummary(type),
    staleTime: 30_000,
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────

export function useTemplates(params: CommTemplateListParams): UseQueryResult<CommTemplateListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'templates', params.page ?? 1, params.type ?? ''],
    queryFn: () => communicationsApi.templates(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateTemplate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateInput) => communicationsApi.createTemplate(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTemplateInput }) =>
      communicationsApi.updateTemplate(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'templates'] });
    },
  });
}

export function useDeleteTemplate() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.deleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'templates'] });
    },
  });
}

// ── Campaigns ─────────────────────────────────────────────────────────────────

const IN_FLIGHT: CampaignStatus[] = ['QUEUED', 'SENDING'];

export function useCampaigns(params: CampaignListParams): UseQueryResult<CampaignListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'campaigns', params.page ?? 1, params.status ?? ''],
    queryFn: () => communicationsApi.campaigns(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
    // A queued / sending campaign changes as the worker sends — follow it.
    refetchInterval: (query) =>
      query.state.data?.data.some((c) => IN_FLIGHT.includes(c.status)) ? 3_000 : false,
  });
}

export function useCampaign(id: string | undefined): UseQueryResult<Campaign | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'campaign', id],
    queryFn: () => (id ? communicationsApi.campaign(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.data && IN_FLIGHT.includes(query.state.data.status) ? 3_000 : false,
  });
}

export function useCreateCampaign() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) => communicationsApi.createCampaign(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'campaigns'] });
    },
  });
}

export function useSendCampaign() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.sendCampaign(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'campaigns'] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'campaign', id] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'stats'] });
    },
  });
}

export function useDeleteCampaign() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.deleteCampaign(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'campaigns'] });
    },
  });
}

// ── Deals ─────────────────────────────────────────────────────────────────────

export function useDeals(page = 1): UseQueryResult<DealListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'deals', page],
    queryFn: () => communicationsApi.deals(page),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateDeal() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDealInput) => communicationsApi.createDeal(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'deals'] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'stats'] });
    },
  });
}

// ── Invoice ───────────────────────────────────────────────────────────────────

export function useSendInvoice() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendInvoiceInput) => communicationsApi.sendInvoice(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'logs'] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'stats'] });
    },
  });
}

export function useBillReceipt(billId: string | undefined): UseQueryResult<BillReceiptData | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'bill-receipt', billId],
    queryFn: () => (billId ? communicationsApi.billReceipt(billId) : Promise.resolve(null)),
    enabled: Boolean(billId),
    staleTime: 60_000,
  });
}

// ── Social Media ──────────────────────────────────────────────────────────────

export function useSocialPosts(page = 1): UseQueryResult<SocialPostListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'social-posts', page],
    queryFn: () => communicationsApi.socialPosts(page),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateSocialPost() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSocialPostInput) => communicationsApi.createSocialPost(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'social-posts'] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'stats'] });
    },
  });
}

// ── Feedback ──────────────────────────────────────────────────────────────────

export function useFeedback(params: FeedbackListParams): UseQueryResult<FeedbackListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'communications', tenantId, 'feedback',
      params.page ?? 1,
      params.rating ?? '',
      params.from ?? '',
      params.to ?? '',
    ],
    queryFn: () => communicationsApi.feedback(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useFeedbackStats(): UseQueryResult<FeedbackStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['communications', tenantId, 'feedback-stats'],
    queryFn: () => communicationsApi.feedbackStats(),
    staleTime: 30_000,
  });
}

export function useMarkFeedbackRead() {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => communicationsApi.markFeedbackRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'feedback'] });
      qc.invalidateQueries({ queryKey: ['communications', tenantId, 'feedback-stats'] });
    },
  });
}

// ── Products ──────────────────────────────────────────────────────────────────

export function useActiveProducts(): UseQueryResult<ProductOption[]> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['products', tenantId, 'active-for-picker'],
    queryFn: () => communicationsApi.activeProducts(),
    staleTime: 120_000,
  });
}
